import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
} from "@mui/material"
import { ReactElement, useState } from "react"

import ChangeRequestMeta from "./changeRequestMeta"
import ChangeSummaryGrids from "./changeSummaryGrids"
import { useApproveRejectChangeRequest } from "../../../services/middleware/hooks/dnoThreshold/useChangeRequestActions"
import { IChangeRequest } from "../../../services/middleware/types/dnoThreshold/dnoThresholdTypes"

type ApproveRejectDialogProps = {
    open: boolean
    changeRequest: IChangeRequest
    onClose: () => void
    onActionSuccess: () => void
}

/**
 * Dialog for approving or rejecting a pending change request.
 * Displays the change summary and requires a comment for rejection.
 */
const ApproveRejectDialog = ({
    open,
    changeRequest,
    onClose,
    onActionSuccess,
}: ApproveRejectDialogProps): ReactElement => {
    const { approve, reject, isProcessing, error } =
        useApproveRejectChangeRequest()
    const [reviewComment, setReviewComment] = useState("")
    const [action, setAction] = useState<"approve" | "reject" | null>(null)

    const handleApprove = async (): Promise<void> => {
        const success = await approve(
            changeRequest.changeRequestId,
            reviewComment
        )
        if (success) {
            setReviewComment("")
            setAction(null)
            onActionSuccess()
        }
    }

    const handleReject = async (): Promise<void> => {
        const success = await reject(
            changeRequest.changeRequestId,
            reviewComment
        )
        if (success) {
            setReviewComment("")
            setAction(null)
            onActionSuccess()
        }
    }

    const handleClose = (): void => {
        setReviewComment("")
        setAction(null)
        onClose()
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Review Change Request — {changeRequest.title}
            </DialogTitle>
            <DialogContent dividers>
                <ChangeRequestMeta
                    changeRequest={changeRequest}
                    showDate
                    canReview={false}
                />

                <ChangeSummaryGrids changes={changeRequest.changes} />

                <TextField
                    label="Reviewer Comment"
                    value={reviewComment}
                    onChange={(e): void => setReviewComment(e.target.value)}
                    fullWidth
                    multiline
                    rows={3}
                    required
                    inputProps={{ maxLength: 500 }}
                    helperText={`${reviewComment.length}/500 characters`}
                    error={reviewComment.length > 500}
                />

                {error && <Alert severity="error">{error}</Alert>}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isProcessing}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="error"
                    disabled={
                        isProcessing ||
                        !reviewComment.trim() ||
                        reviewComment.length > 500
                    }
                    onClick={(): void => {
                        if (action === "reject") {
                            handleReject()
                        } else {
                            setAction("reject")
                        }
                    }}
                >
                    {action === "reject" ? "Confirm Rejection" : "Reject"}
                </Button>
                <Button
                    variant="contained"
                    color="success"
                    disabled={
                        isProcessing ||
                        !reviewComment.trim() ||
                        reviewComment.length > 500
                    }
                    onClick={(): void => {
                        if (action === "approve") {
                            handleApprove()
                        } else {
                            setAction("approve")
                        }
                    }}
                >
                    {action === "approve" ? "Confirm Approval" : "Approve"}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default ApproveRejectDialog
